import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ColDef } from 'ag-grid-community';
import { contractors as contractorsApi } from '@/api/catalogs';
import {
  productionServiceActs,
  type ProductionServiceActListItem,
} from '@/api/productionServiceActs';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import ImportedBadge from '@/shared/components/ImportedBadge';
import DocListFilters, { DocListFiltersRow, useDocListFilters } from '@/shared/components/DocListFilters';
import { useOrgNames, agOrgCol } from '@/shared/hooks/useOrgNames';

const PERMISSION = 'production.service_act.manage';

export default function ProductionServiceActsPage() {
  const navigate = useNavigate();
  const perms = usePermissionsByOrg(PERMISSION);
  const allowedOrgIds = useMemo(
    () => new Set(perms.availableOrgs.map((o) => o.id)),
    [perms.availableOrgs],
  );

  const f = useDocListFilters();
  const orgNames = useOrgNames();

  // Серверна пагінація (Норма #29 — client-side limit 500 не тримає
  // обсяги після клону 1С).
  const { datasource, total } = useInfiniteGridDatasource<ProductionServiceActListItem>(
    (limit, offset, sort) => productionServiceActs.list({
      limit, offset,
      sort_by: sort?.sort_by, sort_dir: sort?.sort_dir,
      organization_id: f.orgId || undefined,
      date_from: f.dateFrom, date_to: f.dateTo,
    }),
    [...f.queryKey],
    { docType: 'doc.production_service_acts' },
  );

  const cQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });
  const cName = useMemo(() => {
    const m = new Map<string, string>();
    cQ.data?.items.forEach((c) => m.set(c.id, c.name));
    return m;
  }, [cQ.data]);

  const fmtAmt = (v: string | null | undefined) =>
    v
      ? Number(v).toLocaleString('uk-UA', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : '';

  const cols = useMemo<ColDef<ProductionServiceActListItem>[]>(() => [
    {
      headerName: '', width: 44, sortable: false,
      cellRenderer: (p: { data?: ProductionServiceActListItem }) =>
        <ImportedBadge source={p.data?.external_source} />,
    },
    { field: 'document_number', headerName: 'Номер', width: 150, sortable: true },
    {
      field: 'document_date', headerName: 'Дата', width: 170, sortable: true,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleString('uk-UA') : '',
    },
    agOrgCol(orgNames),
    {
      field: 'customer_contractor_id', headerName: 'Замовник', sortable: true,
      valueFormatter: (p) => (p.value ? cName.get(p.value as string) ?? '' : ''),
    },
    {
      field: 'service_amount', headerName: 'Сума послуги', width: 150, sortable: false,
      cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => fmtAmt(p.value),
    },
    {
      field: 'total_amount', headerName: 'Разом з ПДВ', width: 150, sortable: true,
      cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => fmtAmt(p.value),
    },
    {
      field: 'posted', headerName: 'Проведено', width: 120, sortable: true,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { posted?: boolean } }) =>
        p.data?.posted ? <span style={{ color: 'var(--badge-green-fg)', fontWeight: 600 }}>✓</span> : '',
    },
  ], [cName, orgNames]);

  const canCreate = f.orgId ? allowedOrgIds.has(f.orgId) : perms.availableOrgs.length > 0;
  const tooltipNew = !canCreate
      ? `Немає прав створювати Акт виробничих послуг у цій юрособі (${PERMISSION})`
      : undefined;

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Акти виробничих послуг<TitleTotal total={total} /></h2>
        <button
          className="primary"
          style={{ marginLeft: 'auto' }}
          disabled={!canCreate}
          title={tooltipNew}
          onClick={() => navigate('/production-service-acts/new')}
        >
          + Новий акт
        </button>
      </div>
      <DocListFiltersRow>
        <DocListFilters f={f} />
      </DocListFiltersRow>
      <InfiniteDocGrid<ProductionServiceActListItem>
        deleteRow={{ remove: (id) => productionServiceActs.remove(id) }}
        datasource={datasource}
        columnDefs={cols}
        storageKey="production-service-acts"
        onRowDoubleClicked={(r) => navigate(`/production-service-acts/${r.id}`)}
        getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : undefined}
      />
    </div>
  );
}
